import { FC } from 'react';
import cx from 'classnames';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Pressable } from '../pressable';

interface PaginationControlsProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  /** Read by screen readers: what is being paged. */
  label?: string;
}

/** Five slots at most, so the row keeps one width while paging and fits the journal's 256px column. */
const SLOTS = 5;

type Slot = number | 'gap';

const pageSlots = (page: number, totalPages: number): Slot[] => {
  if (totalPages <= SLOTS) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (page <= 3) return [1, 2, 3, 'gap', totalPages];
  if (page >= totalPages - 2) return [1, 'gap', totalPages - 2, totalPages - 1, totalPages];
  return [1, 'gap', page, 'gap', totalPages];
};

const chipClasses =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-control border px-2 text-ui tabular-nums transition-colors';
const idleClasses = 'border-line text-ink-muted hover:border-line-strong hover:text-ink';

export const PaginationControls: FC<PaginationControlsProps> = ({
  page,
  totalPages,
  onPageChange,
  className = 'mt-6',
  label = 'Страницы',
}) => {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav aria-label={label} className={cx(className, 'flex items-center justify-center gap-1')}>
      <Pressable
        aria-label="Предыдущая страница"
        className={cx(chipClasses, idleClasses, 'disabled:opacity-40')}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft size={16} aria-hidden />
      </Pressable>
      {pageSlots(page, totalPages).map((slot, index) =>
        slot === 'gap' ? (
          <span key={`gap-${index}`} className="w-4 text-center text-ui text-ink-subtle" aria-hidden>
            …
          </span>
        ) : (
          <Pressable
            key={slot}
            aria-current={slot === page ? 'page' : undefined}
            className={cx(chipClasses, slot === page ? 'border-primary bg-primary text-white' : idleClasses)}
            onClick={() => onPageChange(slot)}
          >
            {slot}
          </Pressable>
        ),
      )}
      <Pressable
        aria-label="Следующая страница"
        className={cx(chipClasses, idleClasses, 'disabled:opacity-40')}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight size={16} aria-hidden />
      </Pressable>
    </nav>
  );
};
